import type { SchemaSite } from './schema-site';
import type { ShopperExperience } from '@/scapi';
import type { EatalyBreadcrumbItem } from '@/components/eataly-breadcrumbs';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';
import type { ContentFolderSearchResult } from '@/lib/api/content-search.server';
import { CONTENT_GROUP, readPageDesignerContentGroup, type EditorialRouteKind } from '@/lib/analytics/content-group';
import { generateBreadcrumbSchema } from './breadcrumb-schema';
import { buildSchemaUrl } from './schema-url';

export type ContentSchemaType = 'CollectionPage' | 'WebPage' | 'Article' | 'NewsArticle';

/**
 * URL slugs are localized, so the type comes only from the route kind and `analyticsContentGroup`.
 * Recipes are Articles: their preparation data is free text, which cannot produce a valid `Recipe`.
 */
export function resolveContentSchemaType(
    kind: EditorialRouteKind,
    page?: Pick<PageWithComponentData, 'data'>
): ContentSchemaType {
    if (kind === 'folderListing' || kind === 'newsListing' || kind === 'contentPillarPage') return 'CollectionPage';
    if (kind === 'locationPage') return 'WebPage';
    switch (readPageDesignerContentGroup(page)) {
        case CONTENT_GROUP.NEWS_DETAILS:
            return 'NewsArticle';
        case CONTENT_GROUP.BLOG:
        case CONTENT_GROUP.MAKERS:
        case CONTENT_GROUP.RECIPES:
            return 'Article';
        default:
            return 'WebPage';
    }
}

export function generateWebsiteSchema(homeUrl: string, name: string, logoUrl: string, site?: SchemaSite) {
    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebSite',
                '@id': `${homeUrl}#website`,
                url: homeUrl,
                name,
                ...(site && { inLanguage: site.language }),
                publisher: { '@id': `${homeUrl}#organization` },
            },
            { '@type': 'OnlineStore', '@id': `${homeUrl}#organization`, url: homeUrl, name, logo: logoUrl },
            {
                '@type': 'WebPage',
                '@id': `${homeUrl}#webpage`,
                url: homeUrl,
                name,
                isPartOf: { '@id': `${homeUrl}#website` },
                ...(site && { inLanguage: site.language }),
            },
        ],
    };
}

function componentsIn(regions: ShopperExperience.schemas['Region'][] = []): ShopperExperience.schemas['Component'][] {
    return regions.flatMap((region) =>
        (region.components ?? []).flatMap((component) => [component, ...componentsIn(component.regions)])
    );
}

function imageUrl(value: unknown, pageUrl: string): string | undefined {
    const image =
        value && typeof value === 'object'
            ? (value as { absURL?: string; url?: string; disBaseLink?: string; link?: string })
            : undefined;
    const source = typeof value === 'string' ? value : image?.absURL || image?.url || image?.disBaseLink || image?.link;
    if (!source) return undefined;
    try {
        const url = new URL(source, pageUrl);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
    } catch {
        return undefined;
    }
}

export function generateContentSchema({
    type,
    page,
    title,
    description,
    pageUrl,
    breadcrumbItems,
    searchResult,
    site,
    lists = { products: [], categories: [] },
}: {
    type: ContentSchemaType;
    page?: PageWithComponentData;
    title: string;
    description?: string | null;
    pageUrl: string;
    breadcrumbItems: EatalyBreadcrumbItem[];
    searchResult?: ContentFolderSearchResult;
    site?: SchemaSite;
    lists?: { products: Record<string, unknown>[]; categories: Record<string, unknown>[] };
}): Record<string, unknown> {
    const components = componentsIn(page?.regions);
    const pathname = new URL(pageUrl).pathname;
    const article = type === 'Article' || type === 'NewsArticle';
    const hero = components.find((component) => component.typeId === 'content.heroTextMedia');
    const heroImage = componentsIn(hero?.regions).find((component) => component.typeId === 'primitives.pdImage');
    const image = imageUrl(heroImage?.data?.src, pageUrl) || imageUrl(page?.data?.photoInCardListing, pageUrl);
    const breadcrumb = generateBreadcrumbSchema(
        pathname.includes('/c/')
            ? breadcrumbItems.map((item) => ({
                  ...item,
                  href:
                      item.href && item.href !== '#'
                          ? buildSchemaUrl({
                                origin: new URL(pageUrl).origin,
                                currentPageUrl: pageUrl,
                                path: item.href,
                            })
                          : item.href,
              }))
            : breadcrumbItems,
        pageUrl,
        site
    );

    const entity = {
        '@type': type,
        '@id': `${pageUrl}#${article ? 'content' : 'webpage'}`,
        url: pageUrl,
        ...(site && { inLanguage: site.language }),
        ...(!article && site && { isPartOf: { '@id': `${site.homeUrl}#website` } }),
        ...(lists.categories.length > 0 && { mentions: lists.categories.map((list) => ({ '@id': list['@id'] })) }),
        ...(lists.products.length > 0 && { mainEntity: lists.products.map((list) => ({ '@id': list['@id'] })) }),
        name: title,
        ...(article && { headline: title }),
        ...(description && { description }),
        ...(image && { image }),
        ...(article ? { mainEntityOfPage: { '@id': `${pageUrl}#webpage` } } : breadcrumb && { breadcrumb }),
        ...(searchResult?.hits.length && {
            mainEntity: {
                '@type': 'ItemList',
                '@id': `${pageUrl}#content-list`,
                numberOfItems: searchResult.total,
                itemListElement: searchResult.hits.map((hit, index) => ({
                    '@type': 'ListItem',
                    position: searchResult.offset + index + 1,
                    item: { '@type': 'WebPage', name: hit.title, url: new URL(hit.path, pageUrl).href },
                })),
            },
        }),
    };
    return {
        '@context': 'https://schema.org',
        '@graph': [
            ...(article
                ? [
                      {
                          '@type': 'WebPage',
                          '@id': `${pageUrl}#webpage`,
                          url: pageUrl,
                          ...(site && { inLanguage: site.language, isPartOf: { '@id': `${site.homeUrl}#website` } }),
                          name: title,
                          mainEntity: { '@id': entity['@id'] },
                          ...(breadcrumb && { breadcrumb }),
                      },
                  ]
                : []),
            entity,
            ...lists.categories,
            ...lists.products,
        ],
    };
}
